import React, { memo, useState } from 'react';
import { Accordion as BSAccordion, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';

export const ACCORDION_TEST_ID = 'accordion';

export type AccordionItem = {
  collapseContainerClassname?: string;
  eventKey: string;
  title: string;
  description?: string;
  element?: JSX.Element;
  TriggerComponent?: any;
  extraTriggerProps?: any;
};

type AccordionProps = {
  defaultActiveKey?: string;
  children: AccordionItem[];
  onClick?(event: React.MouseEvent<HTMLElement>): void;
};

const Accordion: React.FC<AccordionProps> = ({ defaultActiveKey, children, onClick }: AccordionProps) => {
  const [selectedKey, setSelectedKey] = useState<string | null | undefined>(defaultActiveKey);

  const handleSelect = (key: string | null): void => {
    setSelectedKey(key);
  };

  const handleCollapse = (e: React.MouseEvent<HTMLElement>): void => {
    if (onClick) onClick(e);
  };

  const renderBaseHeader = (title: string, description: string | undefined): JSX.Element => {
    return (
      <div className="card-header-container">
        <div className="card-header-title">{title}</div>
        {description && <div className="card-header-description">{description}</div>}
      </div>
    );
  };

  return (
    <BSAccordion data-testid={ACCORDION_TEST_ID} defaultActiveKey={defaultActiveKey} onSelect={handleSelect}>
      {children &&
        children.length > 0 &&
        children.map(
          (
            {
              collapseContainerClassname,
              eventKey,
              title,
              description,
              element,
              TriggerComponent,
              extraTriggerProps,
            }: AccordionItem,
            idx: number,
          ) => (
            <Card key={eventKey}>
              {TriggerComponent ? (
                /* eslint-disable-next-line */
                <TriggerComponent eventKey={eventKey} onClick={handleCollapse} {...extraTriggerProps}>
                  {renderBaseHeader(title, description)}
                </TriggerComponent>
              ) : (
                <BSAccordion.Toggle as={Card.Header} eventKey={eventKey} onClick={handleCollapse}>
                  <>
                    {renderBaseHeader(title, description)}
                    <FontAwesomeIcon icon={selectedKey === eventKey ? faChevronUp : faChevronDown} />
                  </>
                </BSAccordion.Toggle>
              )}
              <BSAccordion.Collapse data-testid={`${ACCORDION_TEST_ID}-collapse-${idx}`} eventKey={eventKey}>
                <Card.Body className={collapseContainerClassname}>{element}</Card.Body>
              </BSAccordion.Collapse>
            </Card>
          ),
        )}
    </BSAccordion>
  );
};

export default memo(Accordion);
